import { Stack, Typography } from '@mui/material';
import type {
  BackupVault,
  InventoryResource,
  Snapshot,
} from '@repo/api-gw-sdk';
import { ResourceType } from '@repo/api-gw-sdk';
import React, { useEffect, useState } from 'react';

import { VerticalSlider } from '@/components/slider/verticalSlider';
import { PitrSnapshotsMultipleDots } from '@/components/snapshots/pitrSnapshotsMultipleDots';
import { useViewParameters } from '@/contexts/navigation';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useWorkspace } from '@/contexts/useWorkspace';
import { isDateValid } from '@/utils/dateTime';
import dayjs from '@/utils/dayjs';

import { SnapshotDrawerHeader } from './snapshotDrawerHeader';
import type { SnapshotsOrigin } from './snapshotOriginToggleButtons';
import { SnapshotRow } from './snapshotRow';
import type { SnapshotsType } from './snapshotsTypesTabs';
import { VMSnapshotRow } from './vmSnapshotRow';

import { OtherSnapshotsList } from '../../../snapshots/convertToEon/otherSnapshotsList';
import { Panels } from '../../panels';

const RestorePanels: Partial<
  Record<
    ResourceType,
    | typeof Panels.RestoreAzureVMWizard
    | typeof Panels.RestoreS3Wizard
    | typeof Panels.RestoreRDSWizard
    | typeof Panels.RestoreDynamoWizard
    | typeof Panels.RestoreMongoAtlasWizard
    | typeof Panels.RestoreEksNamespaceWizard
    | typeof Panels.RestoreAzureSqlDBWizard
    | typeof Panels.RestoreGcpCloudSqlWizard
  >
> = {
  [ResourceType.AwsS3]: Panels.RestoreS3Wizard,
  [ResourceType.AzureStorageAccount]: Panels.RestoreS3Wizard,
  [ResourceType.GcpCloudStorageBucket]: Panels.RestoreS3Wizard,
  [ResourceType.AwsRds]: Panels.RestoreRDSWizard,
  [ResourceType.AwsDynamoDb]: Panels.RestoreDynamoWizard,
  [ResourceType.AtlasMongodbCluster]: Panels.RestoreMongoAtlasWizard,
  [ResourceType.AwsEksNamespace]: Panels.RestoreEksNamespaceWizard,
  [ResourceType.AzureSqlManagedInstance]: Panels.RestoreAzureSqlDBWizard,
  [ResourceType.AzureSqlDatabase]: Panels.RestoreAzureSqlDBWizard,
  [ResourceType.AzureSqlVirtualMachine]: Panels.RestoreAzureVMWizard,
  [ResourceType.GcpCloudSqlInstance]: Panels.RestoreGcpCloudSqlWizard,
};

const VmBasedResourceTypes: ResourceType[] = [
  ResourceType.AwsEc2,
  ResourceType.AzureVirtualMachine,
  ResourceType.AzureSqlVirtualMachine,
  ResourceType.GcpComputeEngineInstance,
];

interface RestoreSnapshotBarProps {
  vaults: BackupVault[];
  eonSnapshots: Snapshot[];
  hasProviderSnapsInDate: boolean;
  showOtherSnapshots: boolean;
  selectedDate: Date;
  entity: InventoryResource;
  onClose: () => void;
}

const NUM_OF_HOURS_IN_SLIDER_STEP = 4;

export const RestoreSnapshotDrawer = (props: RestoreSnapshotBarProps) => {
  const {
    vaults,
    eonSnapshots,
    hasProviderSnapsInDate,
    showOtherSnapshots,
    selectedDate,
    entity,
    onClose,
  } = props;
  const { pitrEnabled, azureSnapshotsDiscoverySupport } = useFeatureFlags();
  const [currentSliderHour, setCurrentSliderHour] = useState<number>(20);
  const [selectedOrigin, setSelectedOrigin] = useState<SnapshotsOrigin>(
    hasProviderSnapsInDate ? 'other' : 'eon'
  );
  const isVmBaseResourceType = VmBasedResourceTypes.includes(
    entity.resourceType
  );
  const [snapshotType, setSnapshotType] = useState<SnapshotsType | undefined>(
    isVmBaseResourceType ? 'vm-based' : undefined
  );
  const [selectedDateSnapshots, setSelectedDateSnapshots] = useState<
    Snapshot[]
  >([]);
  const [selectedSnapshot, setSelectedSnapshot] = React.useState<string>('');

  const restorePanel = RestorePanels[entity.resourceType];

  useEffect(() => {
    const dateSnapshots = selectedDate
      ? (eonSnapshots ?? [])
          .filter((e) => {
            const date = isDateValid(e.pointInTime)
              ? e.pointInTime
              : e.createdTime;

            return dayjs.utc(date).isSame(dayjs.utc(selectedDate), 'date');
          })
          .sort((a, b) => {
            const dateA = isDateValid(a.pointInTime)
              ? a.pointInTime
              : a.createdTime;

            const dateB = isDateValid(b.pointInTime)
              ? b.pointInTime
              : b.createdTime;

            return dateB.getTime() - dateA.getTime();
          })
      : [];
    setSelectedDateSnapshots(dateSnapshots);
    if (dateSnapshots.length <= 0) {
      setSelectedOrigin('other');
    }
  }, [selectedDate, eonSnapshots]);

  useEffect(() => {
    if (snapshotType === 'vm-based') {
      const snapshotsWithoutPiTR = selectedDateSnapshots.filter(
        (snap) => !snap.supportPiTR
      );
      setSelectedSnapshot(snapshotsWithoutPiTR[0]?.id || '');
    } else if (snapshotType === 'database-pitr') {
      const snapshotsWithPiTR = selectedDateSnapshots.filter(
        (snap) => snap.supportPiTR
      );
      setSelectedSnapshot(snapshotsWithPiTR[0]?.id || '');
    }
  }, [selectedDateSnapshots, snapshotType]);

  const shouldShowGroupButtons = !!(
    selectedDateSnapshots.length &&
    hasProviderSnapsInDate &&
    showOtherSnapshots
  );

  const snapshotsWithoutPiTR = selectedDateSnapshots.filter(
    (snap) => !snap.supportPiTR
  );

  const snapshotsWithPiTR = selectedDateSnapshots.filter(
    (snap) => snap.supportPiTR
  );

  const shouldShowSnapsTypeTabs =
    pitrEnabled && isVmBaseResourceType && snapshotsWithPiTR.length > 0;

  const isContainerHeightFixed = snapshotType === 'database-pitr';

  return (
    <Stack
      maxHeight={isContainerHeightFixed ? 'none' : '60%'}
      flexBasis={isContainerHeightFixed ? '60%' : 'fit-content'}
      flexGrow={isContainerHeightFixed ? '0' : undefined}
      flexShrink={isContainerHeightFixed ? '0' : undefined}
      overflow={isContainerHeightFixed ? 'hidden' : undefined}
      data-testid='restore-snapshot-drawer'
      className='w-full'
      direction='column'
      boxShadow='0px 10px 30px 0px rgba(0, 0, 0, 0.2)'
    >
      <Stack
        overflow='hidden'
        height='100%'
        className='w-full'
        direction='column'
      >
        <SnapshotDrawerHeader
          selectedDate={selectedDate}
          shouldShowGroupButtons={shouldShowGroupButtons}
          onSnapshotTypeSelected={setSnapshotType}
          shouldShowSnapsTypeTabs={shouldShowSnapsTypeTabs}
          snapshotType={snapshotType}
          onClose={onClose}
          onSnapshotOriginSelected={setSelectedOrigin}
          selectedOrigin={selectedOrigin}
        />
        <Stack
          direction='column'
          alignItems='center'
          width='100%'
          minHeight='0'
          flexGrow='1'
        >
          {selectedOrigin === 'other' &&
          showOtherSnapshots &&
          hasProviderSnapsInDate &&
          (entity.resourceType === ResourceType.AwsEc2 ||
            (entity.resourceType === ResourceType.AzureVirtualMachine &&
              azureSnapshotsDiscoverySupport)) ? (
            <OtherSnapshotsList
              data-testid='other-snapshots-tab'
              resource={entity}
              selectedDate={selectedDate}
            />
          ) : (
            <>
              {snapshotType === 'vm-based' && (
                <div className='w-full overflow-scroll'>
                  {snapshotsWithoutPiTR.length > 0 ? (
                    snapshotsWithoutPiTR.map((snap) => {
                      return (
                        <VMSnapshotRow
                          resource={entity}
                          key={snap.id}
                          isSelected={selectedSnapshot === snap.id}
                          snap={snap}
                          vault={vaults.find((v) => v.id === snap.vaultId)}
                          onSelect={() =>
                            setSelectedSnapshot(
                              selectedSnapshot === snap.id ? '' : snap.id
                            )
                          }
                          snapshotSelection={snapshotsWithoutPiTR.length > 1}
                        />
                      );
                    })
                  ) : (
                    <VMBasedSnapshotsEmptyState />
                  )}
                </div>
              )}

              {restorePanel && snapshotType !== 'vm-based' && (
                <Stack direction='row' width='100%' height={'100%'}>
                  {snapshotType === 'database-pitr' && (
                    <VerticalSlider
                      numOfSteps={NUM_OF_HOURS_IN_SLIDER_STEP}
                      currentSliderValue={currentSliderHour}
                      setCurrentSliderValue={setCurrentSliderHour}
                    />
                  )}
                  <div className='w-full overflow-scroll'>
                    <SnapshotsRows
                      snapshots={
                        snapshotType === 'database-pitr'
                          ? snapshotsWithPiTR.filter((snap) => {
                              const date = isDateValid(snap.pointInTime)
                                ? snap.pointInTime
                                : snap.createdTime;
                              return (
                                date &&
                                date.getUTCHours() >= currentSliderHour &&
                                date.getUTCHours() <=
                                  currentSliderHour +
                                    NUM_OF_HOURS_IN_SLIDER_STEP
                              );
                            })
                          : snapshotsWithoutPiTR
                      }
                      vaults={vaults}
                      selectedSnapshot={selectedSnapshot}
                      setSelectedSnapshot={setSelectedSnapshot}
                      entity={entity}
                      restorePanel={restorePanel}
                    />
                  </div>
                </Stack>
              )}
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

const VMBasedSnapshotsEmptyState = () => {
  return (
    <Stack height='400px' alignItems='center' justifyContent='center'>
      <Typography variant='body1' width='260px' textAlign='center'>
        {`No VM-based snapshots yet. Backup may still be running — snapshots will
      appear once it's complete.`}
      </Typography>
    </Stack>
  );
};

const PITRSnapshotsEmptyState = () => {
  return (
    <Stack height='100%' alignItems='center' justifyContent='center' gap='8px'>
      <PitrSnapshotsMultipleDots />
      <Typography variant='body1' width='260px' textAlign='center'>
        {`There are no point-in-time recovery snapshots in this time slot yet.`}
      </Typography>
    </Stack>
  );
};

const SnapshotsRows = ({
  snapshots,
  vaults,
  selectedSnapshot,
  setSelectedSnapshot,
  entity,
  restorePanel,
}: {
  snapshots: Snapshot[];
  vaults: BackupVault[];
  selectedSnapshot: string;
  setSelectedSnapshot: (snapshotId: string) => void;
  entity: InventoryResource;
  restorePanel: Required<typeof RestorePanels>[keyof typeof RestorePanels];
}) => {
  const { rightPanel } = useWorkspace();
  const { removeParam } = useViewParameters();

  if (snapshots.length === 0) {
    return <PITRSnapshotsEmptyState />;
  }

  const onClose = () => {
    rightPanel.setIsOpen(false);
    removeParam('id');
  };

  const onBack = () => {
    rightPanel.setComponent({
      panel: Panels.InventoryInstance,
      props: {
        id: entity.id,
        onClose,
        initialTab: 'eon-snapshots',
      },
    });
  };

  return snapshots.map((snap) => {
    return (
      <SnapshotRow
        key={snap.id}
        snap={snap}
        vault={vaults.find((v) => v.id === snap.vaultId)}
        isSelected={selectedSnapshot === snap.id}
        onSelect={() => setSelectedSnapshot(snap.id)}
        resource={entity}
        snapshotSelection={snapshots.length > 1}
        onRestore={() => {
          rightPanel.setComponent({
            panel: restorePanel,
            props: {
              resource: entity,
              snapshot: snap,
              onClose,
              onBack,
            },
          });
        }}
      />
    );
  });
};
